import { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import WorkOrdersKanban from './Kanban/WorkOrdersKanban';
import {
  UserGroupPermissions,
  addPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { TaskType, projectStatusOptions } from '../models';
import { selectEmployees } from '../../employees/reducer';
import { fetchEmployees } from '../../employees/actions';
import { selectCurrentEmployee, selectTaskPrefixes } from '../reducer';
import { selectSiteSettingsSite } from '../../settings/reducer';
import { deleteTaskInEdit, fetchTasks, getTaskPrefixes, setCurrentEmployee } from '../actions';
import { TaskDetails } from '../components/TaskDetails';
import { Drawer } from '../../../core/components/Drawer';
import WorkOrderTable from './WorkOrderTable/WorkOrderTable';
import { TASK_DRAWER_WIDTH } from '../types';
import { getApiStatus } from '../../../core/utils';
import { IoCalendarOutline, IoSearchSharp } from 'react-icons/io5';
import { FaRegCircleCheck, FaRegClock, FaRegUser } from 'react-icons/fa6';
import { FaRegFolderOpen, FaMapMarkerAlt } from 'react-icons/fa';
import { PageFilters, FilterConfig, FilterPlacement } from '../../../core/components/PageFilters';
import { selectMultiSiteSelection } from '../../site-selection/reducer';

enum WorkOrderView {
  Pending = 'backlog',
  Completed = 'table',
  Kanban = 'kanban',
  All = 'all',
}

export const WorkOrders = () => {
  const [view, setView] = useState<WorkOrderView>(WorkOrderView.Pending);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [showOnHold, setShowOnHold] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees) as any[];
  const currentSite = useSelector(selectSiteSettingsSite);
  const multiSiteSelection = useSelector(selectMultiSiteSelection);
  const taskPrefixList = useSelector(selectTaskPrefixes) as any[];
  const currentEmployee = useSelector(selectCurrentEmployee);
  const [isAdding, setIsAdding] = useState(false);
  const [tableRefreshToken, setTableRefreshToken] = useState(0);
  const isTasksFetching = useSelector((state: any) => getApiStatus(state, 'GET_TASK'));

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, text: `${e.firstName} ${e.lastName}` })),
    [employees]
  );

  const locationOptions = useMemo(() => {
    const locations = [{ value: currentSite.id, text: currentSite.externalId }];
    const multiLocations = multiSiteSelection.map((s: any) => ({
      value: s.id,
      text: s.externalId,
    }));
    return [...locations, ...multiLocations];
  }, [currentSite, multiSiteSelection]);

  const statusOptions = projectStatusOptions.map((s: string) => {
    return {
      value: s,
      text: s,
      selected: s == 'New',
    };
  });

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'status',
        type: 'select',
        name: 'Status',
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegCircleCheck />,
        options: statusOptions,
        onChange: (values: string[]) => {
          setFilters((prev: any) => ({ ...prev, statusFilter: values }));
        },
      },
      {
        id: 'dueDate',
        type: 'date',
        name: 'Due Date',
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegClock />,
        onChange: (date: Date | null) =>
          setFilters((prev: any) => ({ ...prev, dateQuery: date ? date.toISOString() : '' })),
      },
      {
        id: 'name',
        type: 'search',
        name: 'Name',
        placement: FilterPlacement.Menu,
        icon: <IoSearchSharp />,
        defaultVisible: true,
        onChange: (value: string | null) =>
          setFilters((prev: any) => ({ ...prev, nameQuery: value })),
      },
      {
        id: 'assignee',
        type: 'select',
        name: 'Assignee',
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegUser />,
        options: [{ value: 'Unassigned', text: 'Unassigned' }, ...employeeOptions],
        onChange: (values: string[]) =>
          setFilters((prev: any) => ({ ...prev, memberFilter: values })),
      },
      {
        id: 'createdAt',
        type: 'date',
        name: 'Created',
        placement: FilterPlacement.Menu,
        icon: <IoCalendarOutline />,
        defaultVisible: true,
        onChange: (date: Date | null) =>
          setFilters((prev: any) => ({
            ...prev,
            createdDateQuery: date ? date.toISOString() : '',
          })),
      },
      {
        id: 'site',
        type: 'select',
        name: 'Location',
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <FaMapMarkerAlt />,
        options: locationOptions,
        onChange: (values: string[]) =>
          setFilters((prev: any) => ({ ...prev, locationFilter: values })),
      },
      {
        id: 'prefix',
        type: 'select',
        name: 'Category',
        placement: FilterPlacement.Menu,
        icon: <FaRegFolderOpen />,
        defaultVisible: true,
        options: (taskPrefixList as any[]).map((p) => ({
          value: String(p.type),
          text: p.message,
        })),
        onChange: (values: string[]) =>
          setFilters((prev: any) => ({ ...prev, prefixFilter: values })),
      },
    ],
    [employeeOptions, taskPrefixList, locationOptions]
  );

  const [filters, setFilters] = useState<any>({
    nameQuery: '',
    dateQuery: '',
    createdDateQuery: '',
    statusFilter: ['New'],
    priorityFilter: [],
    memberFilter: [],
    prefixFilter: [],
    locationFilter: [],
  });

  const handleOpenTask = (task: any) => {
    if (!task) {
      setSelectedTask(null);
      return;
    }
    const normalized = {
      ...task,
      _id: task.id,
      _taskId: task.id,
      quickAddTask: false,
    };
    setSelectedTask(normalized);
  };

  const handleAddWorkOrder = () => {
    dispatch(deleteTaskInEdit());
    setSelectedTask(null);
    setIsAdding(true);
  };

  const addWorkOrderButton = (
    <UserGroupPermissions itemRef={addPermissionRefs.task}>
      <button className="button primary" onClick={handleAddWorkOrder}>
        <span className="desktop-label">{intl.formatMessage({ id: 'addWorkOrder' })}</span>
        <span className="mobile-label">+</span>
      </button>
    </UserGroupPermissions>
  );

  const handleCloseDrawer = () => {
    setIsAdding(false);
    setSelectedTask(null);
  };

  // The Kanban reads the shared tasks slice, the table reads projectTasks —
  // refreshing after a drawer save/delete has to cover both.
  const refreshWorkOrders = () => {
    dispatch(fetchTasks({ page: 0, type: 'Project' }));
    setTableRefreshToken((token) => token + 1);
  };

  useEffect(() => {
    dispatch(fetchTasks({ page: 0, type: 'Project' }));
    dispatch(fetchEmployees());
    if (!taskPrefixList.length) {
      dispatch(getTaskPrefixes());
    }
  }, [currentSite, multiSiteSelection]);

  useEffect(() => {
    if (!currentEmployee && employees.length) {
      dispatch(setCurrentEmployee(employees[0].id));
    }
  }, [currentEmployee, employees, dispatch]);

  return (
    <>
      <div className="filters-actions-container mb-3">
        <PageFilters filters={filterConfigs} maxChicletFilters={4} />
        <div className="page-actions">{addWorkOrderButton}</div>
      </div>
      {view === WorkOrderView.Kanban ? (
        <WorkOrdersKanban filters={filters} onOpenTask={handleOpenTask} showOnHold={showOnHold} />
      ) : (
        <div className="tasks-table">
          <WorkOrderTable
            headerActions={null}
            onOpenTask={handleOpenTask}
            highlightedRow={selectedTask?._id || selectedTask?.id}
            externalFilters={filters}
            filterBarVisible={false}
            refreshToken={tableRefreshToken}
          />
        </div>
      )}
      <Drawer
        expanded={!!selectedTask || isAdding}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        loading={isTasksFetching}
        onBackdropClick={handleCloseDrawer}
      >
        <TaskDetails
          taskId={selectedTask?.id}
          isNew={isAdding}
          type={TaskType.Project}
          onClose={handleCloseDrawer}
          onSave={refreshWorkOrders}
          onDelete={refreshWorkOrders}
        />
      </Drawer>
    </>
  );
};
export default WorkOrders;
